import { useEffect, useRef } from "react";

/**
 * Ambient particle-network canvas for the hero. Nodes drift, link when close,
 * and brighten near the cursor. Node count scales with viewport; honors
 * reduced motion (renders a static field). Returns a ref for the <canvas>.
 */
const useHeroCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const ctx = canvas.getContext("2d");
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const section = canvas.parentElement;

    let nodes = [];
    let w = 0;
    let h = 0;
    let raf = 0;
    const mouse = { x: 0, y: 0, active: false };
    const LINK = 150;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      if (!w || !h) {
        return;
      }
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(24, Math.min(w < 700 ? 40 : 90, Math.floor((w * h) / 19000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: 0.9 + Math.random() * 2.1,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const n of nodes) {
        if (!reduce) {
          n.x += n.vx;
          n.y += n.vy;
        }
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
        let boost = 0;
        if (mouse.active) {
          boost = Math.max(0, 1 - Math.hypot(n.x - mouse.x, n.y - mouse.y) / 260);
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + boost * 1.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(150,196,255,${(0.34 + boost * 0.5).toFixed(3)})`;
        ctx.fill();
      }
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d > LINK) continue;
          let al = (1 - d / LINK) * 0.17;
          if (mouse.active) {
            const inf = Math.max(
              0,
              1 -
                Math.min(
                  Math.hypot(a.x - mouse.x, a.y - mouse.y),
                  Math.hypot(b.x - mouse.x, b.y - mouse.y)
                ) /
                  240
            );
            al += inf * 0.42;
          }
          ctx.strokeStyle = `rgba(93,150,255,${Math.min(al, 0.7).toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      raf = window.requestAnimationFrame(draw);
    };

    const onMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    if (section) {
      section.addEventListener("mousemove", onMove);
      section.addEventListener("mouseleave", onLeave);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (section) {
        section.removeEventListener("mousemove", onMove);
        section.removeEventListener("mouseleave", onLeave);
      }
    };
  }, []);

  return canvasRef;
};

export default useHeroCanvas;
